import { Navigate } from 'react-router-dom';
import LoadingSpinner from '../../components/common/loading/LoadingSprinner';
import { useAuth } from '../../../context/AuthContext';
import { ROLE } from '../../../Role';

const ProtectedRoute = ({
  children,
  allowedRoles,
  requireBusinessScope = false,
}: {
  children: React.ReactNode;
  allowedRoles: string[];
  /**
   * Multi-business support (Phase 1) — per approved_architecture.md §4.3.
   * When true, the route requires an active business scope. The backend's
   * requireBusinessScope middleware provides server-side enforcement; this
   * flag provides the client-side gate.
   */
  requireBusinessScope?: boolean;
}) => {
  const {
    isAuthenticated,
    loading,
    role,
    customerAccountStatus,
    activeBusinessId,
  } = useAuth();
  const roles = Array.isArray(role) ? role : role ? [role] : [];

  if (loading) {
    return <LoadingSpinner label="Loading.." />;
  }

  if (!isAuthenticated || roles.length === 0) {
    return <Navigate to="/home" replace />;
  }

  const isCustomer = roles.includes(ROLE.CUSTOMER);
  const hasValidStatus = ['active', 'deactivated'].includes(
    customerAccountStatus ?? ''
  );

  if (isCustomer && !hasValidStatus) {
    return <Navigate to="/home" replace />;
  }

  const hasAccess = roles.some((r) => allowedRoles.includes(r));

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  // Multi-business scope gate (Phase 1) — client-side equivalent of
  // requireBusinessScope from @aplicy-com/aplicy-auth-middleware v1.2.0+.
  // The backend validates the JWT businessIds[] claim; this gate prevents
  // the UI from rendering before the user has selected a business context.
  if (requireBusinessScope && !activeBusinessId) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
